import { IRawUnit, TApiUnit, TRawUnit } from "@propely/propely-typings";
import { getStringArray } from "@src/utils/arrayUtilities";
import { collection, doc, getDoc, getFirestore } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useMemo } from "react";
import { useQueries, useQuery } from "react-query";
import { z } from "zod";
import { propelyApi } from "../../libs/firebase/api";
import { useRealtimeCollectionData } from "../generic/useRealtimeCollectionData";

export type GetPropertyUnitByIDInput = {
  propertyID: string;
  unitID: string;
};

export const usePropertyUnitByIDKey = (input: GetPropertyUnitByIDInput) => ["properties", { propertyID: input.propertyID, unitID: input.unitID }];

export const fetchPropertyUnit = async (input: GetPropertyUnitByIDInput) => {
  const propertyUnit = await getDoc(doc(getFirestore(), "properties", input.propertyID, "units", input.unitID));
  return TRawUnit.parse({
    id: propertyUnit.id,
    ...propertyUnit.data(),
  });
};

export const usePropertyUnitByID = (input: GetPropertyUnitByIDInput) => {
  const $propertyUnit = useQuery(usePropertyUnitByIDKey(input), () => fetchPropertyUnit(input));
  return {
    $propertyUnit,
    propertyUnit: $propertyUnit.data,
  };
};

export const useRealtimePropertyUnits = (propertyID: string) => {
  const query = useMemo(() => collection(getFirestore(), `properties/${propertyID}/units`), [propertyID]);
  const { data, loading, error } = useRealtimeCollectionData<IRawUnit>(query, TRawUnit);
  return {
    propertyUnits: data,
    loading,
    error,
  };
};

export const useUnitsInProperty = (propertyID: string, unitIDs: string[]) => {
  return usePropertyUnits(unitIDs.map((unitID) => ({ propertyID, unitID })));
};

export const usePropertyUnits = (unitIDs: { unitID: string; propertyID: string }[]) => {
  const $propertyUnits = useQueries(
    unitIDs.map(({ propertyID, unitID }) => ({
      queryKey: usePropertyUnitByIDKey({ propertyID, unitID }),
      queryFn: () => fetchPropertyUnit({ propertyID, unitID }),
    }))
  );

  const propertyUnits = $propertyUnits.map(($propertyUnit) => $propertyUnit.data).filter((data): data is IRawUnit => Boolean(data));
  const isLoading = $propertyUnits.some(($propertyUnit) => $propertyUnit.isLoading);

  return {
    $propertyUnits,
    propertyUnits,
    isLoading,
  };
};

type GetApiUnitsFilters = {
  archived?: boolean;
  propertyID?: string[] | string;
};

export const apiUnitsQueryKey = (filters: GetApiUnitsFilters) => [
  "get-api-units",
  {
    archived: filters.archived,
    propertyID: filters.propertyID,
  },
];

const fetchAPIUnits = async (filters: GetApiUnitsFilters) => {
  const queryString = new URLSearchParams();
  if (filters.archived !== undefined) queryString.append("archived", filters.archived.toString());
  if (filters.propertyID) queryString.append("propertyID", getStringArray(filters.propertyID).join(","));

  const response = await propelyApi().get(`/units?${queryString.toString()}`);
  return z.array(TApiUnit).parse(response.data.datas);
};

type QueryOptions = {
  refetchOnMount?: boolean;
  enabled?: boolean;
};

export const useAPIUnits = (filters: GetApiUnitsFilters = {}, options: QueryOptions = {}) => {
  const $units = useQuery(apiUnitsQueryKey(filters), () => fetchAPIUnits(filters), options);
  const units = $units.data;
  return { $units, units };
};

export type UpdateUnitInput = {
  propertyId: string;
  unitId: string;
  data: { [key: string]: { value: any; suffix?: string } };
};
export const updatePropertyUnit = (input: UpdateUnitInput) => {
  const functions = getFunctions(undefined, "europe-west1");
  const updateTenantInfoFields = httpsCallable(functions, "updateTenantInfoFields");
  return updateTenantInfoFields({
    propertyID: input.propertyId,
    unitID: input.unitId,
    fields: input.data,
  });
};

interface DuplicatePropertyUnitInput {
  fromPropertyID: string;
  toPropertyID: string | undefined;
  unitID: string;
  name: string | undefined;
}

const TApiDuplicatePropertyUnitResponse = z.object({
  id: z.string(),
  propertyID: z.string(),
});
type IDuplicatePropertyUnitResponse = z.infer<typeof TApiDuplicatePropertyUnitResponse>;

export const duplicatePropertyUnit = async ({
  fromPropertyID,
  toPropertyID,
  unitID,
  name,
}: DuplicatePropertyUnitInput): Promise<IDuplicatePropertyUnitResponse> => {
  const body: { [key: string]: any } = { name: name };
  if (toPropertyID) {
    body.toPropertyID = toPropertyID;
  }

  const result = await propelyApi().post(`/properties/${fromPropertyID}/units/${unitID}/duplicate`, body);
  return TApiDuplicatePropertyUnitResponse.parse(result.data);
};
